import { useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { fetchAdminApps, fetchRoleKeys, fetchRoleMapCoverage, fetchRoleMaps } from '@/api/console';

/**
 * EAA-1817: the four queries every part of `/console/role-coverage` reads
 * from, plus the capability flags that gate them.
 *
 * EAA-1817: this board is DELIBERATELY not org-scoped — `fetchRoleMapCoverage`
 * (`RoleMapService.coverage()`) filters `organization__isnull=True`
 * server-side and takes no organization param at all, so it only ever
 * reflects ECOSYSTEM-DEFAULT links. `fetchRoleMaps()` below is filtered the
 * same way client-side (`organization_id === null`) so the editor's chip ids
 * agree with what the matrix shows. Do not add an acting-org scope switch to
 * this screen — there is nothing for it to switch between.
 *
 * GATE TRAP (see `api/console.ts` docstrings): `GET /admin/app-roles` is gated on
 * `APPS_VIEW` — `AdminAppRolesView` carries
 * `@require_permissions_for_method(get=(APPS_VIEW,), post=(APPS_MANAGE,))`, not a
 * superadmin check. What still holds is the part that made this matter: the app-role
 * catalog is per-application and sits behind a DIFFERENT permission from the
 * `console.rolemap.*` set this screen runs on, so a caller who may write links is not
 * guaranteed to be allowed to read a catalog. `canManage` (`console.rolemap.manage`,
 * the capability that already gates every default-scope RoleMap write elsewhere in
 * this console — `roleMap/useRoleMapTable.ts`) remains the gate for the "+ vai trò"
 * picker and the "Xem danh mục" detail view: both only mean anything to someone who
 * can write links, and it keeps the catalog fetch off the wire for everyone else.
 *
 * EAA-1817: that gate may therefore be MORE RESTRICTIVE than the server requires — it
 * was chosen while this comment claimed the endpoint was superadmin-only. Relaxing it
 * to an apps-catalog capability changes who sees the picker and the catalog view, so
 * it is a product decision rather than a comment fix, and is left as-is here.
 *
 * EAA-1817: the `IsSuperAdmin` claim above had been repeated in ~8 comments across
 * `RoleCoverage/` and `RoleMap/` and was corrected wholesale under this ticket. Every
 * role-map endpoint is a fine-grained `@require_permissions` /
 * `@require_permissions_for_method` gate (`ROLEMAP_VIEW`, `ROLEMAP_MANAGE`,
 * `ORG_ROLEMAP_VIEW`, `APPS_VIEW`); not one of them is a superadmin check. If a doc
 * or a review still quotes the old wording, the decorators in portal-api
 * `directory/rolemap_views.py` and `directory/console_views.py` are what is true.
 */
export function useRoleCoverageData(active = true) {
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();

    const canView = can('console.rolemap.view');
    const canManage = can('console.rolemap.manage');
    const canSync = can('console.rolemap.sync');
    /**
     * EAA-1817: `POST /api/v1/role-keys` — creating a standard role — is gated on
     * `console.rolekeys.manage`, NOT `console.rolemap.manage`. They are different
     * permissions and belong to different groups server-side; the vocabulary is
     * ecosystem-wide, the links are not.
     *
     * The endpoint ALSO runs an inner `IsSuperAdmin()` check on top of the
     * decorator (`StandardRoleListCreateView.post`), so holding this capability
     * is necessary but not sufficient. The console cannot see that second
     * condition — there is no superadmin flag in the capability set — so the
     * control is offered to anyone holding the capability and a refusal surfaces
     * as the endpoint's own error rather than a hidden button. Hiding it from
     * everyone who merely MIGHT be refused would hide it from the people who can.
     */
    const canManageRoleKeys = can('console.rolekeys.manage');
    // EAA-1841: false while the merged screen is showing the list view — see
    // `useRoleMapTable`'s `active` note for why gating queries (not the hook
    // call) is what stops the hidden view from fetching.
    const enabled = active && authenticated && Boolean(accessToken) && canView;
    const apiBase = config.apiBase;
    const token = accessToken;

    const coverageQuery = useQuery({
        queryKey: ['role-map-coverage', accessToken],
        queryFn: () => fetchRoleMapCoverage(apiBase, token as string),
        enabled,
        retry: false,
    });

    const roleKeysQuery = useQuery({
        queryKey: ['role-keys', accessToken],
        queryFn: () => fetchRoleKeys(apiBase, token as string),
        enabled,
        retry: false,
    });

    const adminAppsQuery = useQuery({
        queryKey: ['admin-apps', accessToken],
        queryFn: () => fetchAdminApps(apiBase, token as string),
        enabled,
        retry: false,
    });

    // EAA-1817: the flat rows back the editor card only (chip ids, staleness,
    // per-app link counts) — the matrix itself reads `coverageQuery` alone.
    const roleMapsQuery = useQuery({
        queryKey: ['role-maps', accessToken],
        queryFn: () => fetchRoleMaps(apiBase, token as string),
        enabled,
        retry: false,
    });

    return {
        apiBase,
        token,
        canView,
        canManage,
        canSync,
        canManageRoleKeys,
        enabled,
        coverageQuery,
        roleKeysQuery,
        adminAppsQuery,
        roleMapsQuery,
    };
}

export type RoleCoverageData = ReturnType<typeof useRoleCoverageData>;
